// Wire-format types shared between the web app and the api. Keep these in
// sync with the Prisma schema and the route response shapes.

// P6 Section 1: speaker-aware transcript.
export type SpeakerRole = 'ME' | 'COUNSEL' | 'BENCH' | 'WITNESS' | 'OTHER';
export const SPEAKER_ROLES: SpeakerRole[] = ['ME', 'COUNSEL', 'BENCH', 'WITNESS', 'OTHER'];

export interface Turn {
  /** Diarization speaker index (0,1,2,…); -1 for an untagged/unknown speaker. */
  speaker: number;
  text: string;
}

export type SpeakerRoleMap = Record<string, SpeakerRole>;

export interface ResponseRecord {
  id: string;
  sessionId: string;
  prompt: string;
  label: string | null;
  answer: string;
  toolsUsed: ToolUseRecord[];
  durationMs: number;
  /** P2 Section 2: full provenance for "Show audit". Optional for old rows. */
  auditTrail?: ResponseAuditTrail | null;
  /** ISO 8601 string. */
  createdAt: string;
}

// Mirrors services/audit-trail.ts AuditTrail (+ firstTokenMs stamped by the
// route). All fields optional-tolerant so a pre-P2 row with `{}` renders fine.
export interface ResponseAuditTrail {
  mode?: 'fast' | 'deep' | 'live';
  legalSkills?: Array<{ slug: string; name: string }>;
  legalSkillsTrimmed?: Array<{ slug: string; name: string }>;
  knowledgeEntries?: Array<{ title: string; sourceType: string }>;
  documents?: Array<{ filename: string }>;
  // P5 S2 / P6 S2-S3: how documents were selected + the chunks that grounded the
  // answer, so the grounding footer can be honest.
  retrievalMode?: 'semantic' | 'lexical' | 'hybrid' | 'recency';
  retrievedChunks?: Array<{ documentId: string; filename: string; chunkIndex: number; similarity: number; tokens: number }>;
  // Legal Brain A4: verified authorities the answer could cite (Authority chips).
  authorities?: Array<{
    id: string;
    type: 'LEGISLATION' | 'CASE';
    title: string;
    citation: string;
    jurisdiction: string;
    pinned: boolean;
    tokens: number;
  }>;
  mcpToolsCalled?: Array<{ server: string; tool: string }>;
  citationsVerifiedViaWebSearch?: string[];
  verifiedPoolSize?: number;
  flaggedUnverified?: boolean;
  usage?: {
    inputTokens: number;
    outputTokens: number;
    cacheCreationInputTokens: number;
    cacheReadInputTokens: number;
  };
  durationMs?: number;
  firstTokenMs?: number;
  cost?: {
    usd: number;
    rateNote: string;
    rates?: Record<string, number>;
  };
}

export interface AskTiming {
  firstTokenMs: number;
  totalMs: number;
}

export interface ToolUseRecord {
  server: string;
  tool: string;
  input?: unknown;
}

export interface AskUsage {
  inputTokens: number;
  outputTokens: number;
  cacheCreationInputTokens?: number;
  cacheReadInputTokens?: number;
}

export interface AskResponseEnvelope {
  response: ResponseRecord;
  usage: AskUsage;
  stopReason: string | null;
  /** P2 Section 2: honest measured timings for the streaming card footer. */
  timing?: AskTiming;
}

export interface MatterSummary {
  id: string;
  name: string;
  reference: string | null;
  court: string | null;
  archived: boolean;
}

export interface MatterDetail extends MatterSummary {
  context: string;
  /** The matter's single winning point. Null when none is set. */
  anchorPoint: string | null;
  dropboxPath: string | null;
  sharepointPath: string | null;
  emailKeywords: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MatterInput {
  name: string;
  court?: string | null;
  reference?: string | null;
  context: string;
  anchorPoint?: string | null;
  dropboxPath?: string | null;
  sharepointPath?: string | null;
  emailKeywords?: string | null;
}

export interface SessionSummary {
  id: string;
  matterId: string;
  startedAt: string;
  endedAt: string | null;
  /** Courtroom-ready sprint: true for practice sessions (rehearsal mode). */
  isRehearsal?: boolean;
  /** Optional on the wire; present on GET /sessions/:id, absent on POST. */
  transcript?: string;
}

export interface AuthUser {
  id: string;
  email: string;
}
